import React from 'react';
import PropTypes from 'prop-types';
import OmForm from '../../ui/OmForm';
import OmButton from '../../ui/OmButton';
import { getClaimTypeDisplayName } from '../../../services/progressiveDisclosureService';

/**
 * Step 5: Claim Summary Component
 * 
 * Dedicated step for users to review and verify all claim information
 * before final submission.
 * 
 * Features:
 * - Comprehensive claim data display organized by sections
 * - Type-specific information based on claim type
 * - Edit navigation buttons to go back to specific steps
 * - Final verification step before submission
 */
const Step5ClaimSummary = ({
  formData,
  errors,
  isLoading,
  onNext,
  onPrevious,
  onEdit,
  uploadedFiles = []
}) => {

  // Get formatted claim type display name
  const getClaimTypeDisplay = () => {
    if (!formData.type) return 'Not selected';
    return getClaimTypeDisplayName(formData.type);
  };

  // Get type-specific details based on claim type
  const getTypeSpecificDetails = () => {
    const details = [];
    
    switch (formData.type) {
      case 'DEATH':
        if (formData.dateOfDeath) details.push({ label: 'Date of Death', value: formData.dateOfDeath });
        if (formData.causeOfDeath) details.push({ label: 'Cause of Death', value: formData.causeOfDeath });
        if (formData.placeOfDeath) details.push({ label: 'Place of Death', value: formData.placeOfDeath });
        if (formData.relationshipToDeceased) details.push({ label: 'Relationship to Deceased', value: formData.relationshipToDeceased });
        if (formData.burialCosts) details.push({ label: 'Burial Costs', value: `₵${parseFloat(formData.burialCosts).toLocaleString()}` });
        break;
        
      case 'DISABILITY':
        if (formData.disabilityType) details.push({ label: 'Disability Type', value: formData.disabilityType });
        if (formData.dateOfDisability) details.push({ label: 'Date of Disability', value: formData.dateOfDisability });
        if (formData.causeOfDisability) details.push({ label: 'Cause of Disability', value: formData.causeOfDisability });
        if (formData.estimatedPaymentDuration) details.push({ label: 'Estimated Payment Duration', value: formData.estimatedPaymentDuration });
        if (formData.currentEmploymentStatus) details.push({ label: 'Current Employment Status', value: formData.currentEmploymentStatus });
        break;
        
      case 'CRITICAL_ILLNESS':
        if (formData.diagnosisDate) details.push({ label: 'Diagnosis Date', value: formData.diagnosisDate });
        if (formData.conditionName) details.push({ label: 'Condition Name', value: formData.conditionName });
        if (formData.conditionSeverityStage) details.push({ label: 'Condition Severity/Stage', value: formData.conditionSeverityStage });
        if (formData.treatmentStartDate) details.push({ label: 'Treatment Start Date', value: formData.treatmentStartDate });
        if (formData.treatingPhysician) details.push({ label: 'Treating Physician', value: formData.treatingPhysician });
        break;
        
      default:
        // For other claim types, show any additional fields that exist
        Object.entries(formData).forEach(([key, value]) => {
          if (value && !['type', 'policyNumber', 'claimantName', 'claimantMobile', 'claimantEmail', 'sumAssured', 'accountNumber', 'bankName', 'branch', 'bankCode', 'assignedToId'].includes(key)) {
            const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
            details.push({ label, value: value.toString() });
          }
        });
    }
    
    return details;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    // Prevent multiple submissions
    if (isLoading) {
      return;
    }
    onNext();
  };

  const typeSpecificDetails = getTypeSpecificDetails();

  return (
    <div className="space-y-6 relative">
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
          <div className="text-center">
            <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
            <p className="text-steel font-medium">Submitting your claim...</p>
            <p className="text-steel text-sm mt-1">Please do not close this window</p>
          </div>
        </div>
      )}
      
      <OmForm
        titleText="Review and Submit Your Claim"
        descriptionText="Please review all the information below and submit your claim for processing. You can edit any section by clicking the 'Edit' button."
        onSubmit={handleSubmit}
        formId="claim-summary-form"
        submitButtonLabel={isLoading ? "Submitting Claim..." : "Submit Claim"}
        hideSubmitButton={false}
        isLoading={isLoading}
      >
        <div className="space-y-6">
          {/* Benefit Summary (server-computed) */}
          {typeof formData.benefitAmount !== 'undefined' && formData.benefitAmount !== null && (
            <div className="bg-crystal-white rounded-lg p-6 border border-woodland-moss/20">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-deep-jungle text-lg">Benefit Summary</h4>
              </div>
              <div className="text-sm">
                <span className="font-medium text-steel">Benefit Amount:</span>{' '}
                <span className="text-deep-jungle">₵{parseFloat(formData.benefitAmount).toLocaleString()}</span>
              </div>
            </div>
          )}

          {/* Policy Information Section */}
          <div className="bg-crystal-white rounded-lg p-6 border border-woodland-moss/20">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-deep-jungle text-lg">Policy Information</h4>
              <button
                type="button"
                onClick={() => onEdit(1)}
                className="text-fresh-leaf hover:text-woodland-moss text-sm font-medium transition-colors"
              >
                Edit Policy Info
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div><span className="font-medium text-steel">Policy Number:</span> <span className="text-deep-jungle">{formData.policyNumber || 'Not provided'}</span></div>
              <div><span className="font-medium text-steel">Claim Type:</span> <span className="text-deep-jungle">{getClaimTypeDisplay()}</span></div>
              <div><span className="font-medium text-steel">Staff Number:</span> <span className="text-deep-jungle">{formData.memberStaffNumber || 'Not provided'}</span></div>
              <div><span className="font-medium text-steel">Date Filed:</span> <span className="text-deep-jungle">{formData.dateFiled || 'Not provided'}</span></div>
              <div><span className="font-medium text-steel">Active Policy Holder:</span> <span className="text-deep-jungle">{formData.isActive === true ? 'Yes' : formData.isActive === false ? 'No' : 'Unknown'}</span></div>
            </div>
          </div>

          {/* Personal Information Section */}
          <div className="bg-crystal-white rounded-lg p-6 border border-woodland-moss/20">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-deep-jungle text-lg">Personal Information</h4>
              <button
                type="button"
                onClick={() => onEdit(2)}
                className="text-fresh-leaf hover:text-woodland-moss text-sm font-medium transition-colors"
              >
                Edit Personal Info
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div><span className="font-medium text-steel">Claimant Name:</span> <span className="text-deep-jungle">{formData.claimantName || 'Not provided'}</span></div>
              <div><span className="font-medium text-steel">Mobile:</span> <span className="text-deep-jungle">{formData.claimantMobile || 'Not provided'}</span></div>
              <div><span className="font-medium text-steel">Email:</span> <span className="text-deep-jungle">{formData.claimantEmail || 'Not provided'}</span></div>
              <div><span className="font-medium text-steel">Bank Account:</span> <span className="text-deep-jungle">{formData.accountNumber ? `${formData.bankName} - ${formData.accountNumber}` : 'Not provided'}</span></div>
            </div>
          </div>

          {/* Claim-Specific Details Section */}
          {typeSpecificDetails.length > 0 && (
            <div className="bg-crystal-white rounded-lg p-6 border border-woodland-moss/20">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-deep-jungle text-lg">{getClaimTypeDisplay()} Details</h4>
                <button
                  type="button"
                  onClick={() => onEdit(3)}
                  className="text-fresh-leaf hover:text-woodland-moss text-sm font-medium transition-colors"
                >
                  Edit Claim Details
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {typeSpecificDetails.map((detail, index) => (
                  <div key={index}>
                    <span className="font-medium text-steel">{detail.label}:</span>{' '}
                    <span className="text-deep-jungle">{detail.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Documents Summary Section */}
          <div className="bg-crystal-white rounded-lg p-6 border border-woodland-moss/20">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-deep-jungle text-lg">Documents Uploaded</h4>
              <button
                type="button"
                onClick={() => onEdit(4)}
                className="text-fresh-leaf hover:text-woodland-moss text-sm font-medium transition-colors"
              >
                Edit Documents
              </button>
            </div>
            {uploadedFiles.length > 0 ? (
              <div className="space-y-3">
                <div className="text-sm text-steel">
                  <span className="font-medium">Total Documents:</span> {uploadedFiles.length}
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-silver-mist/30 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-fresh-leaf/20 rounded flex items-center justify-center">
                          <svg className="w-4 h-4 text-fresh-leaf" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div>
                          <div className="font-medium text-deep-jungle text-sm">{file.name}</div>
                          <div className="text-steel text-xs">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                            {file.isRequired && <span className="ml-2 text-green-600 font-medium">• Required</span>}
                          </div>
                        </div>
                      </div>
                      <div className="text-green-600 text-sm font-medium">
                        ✓ Uploaded
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="w-12 h-12 bg-silver-mist rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-steel" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-steel text-sm">No documents uploaded</p>
              </div>
            )}
          </div>

          {/* General Errors */}
          {errors.general && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{errors.general}</p>
            </div>
          )}
        </div>

        {/* Previous Button in actions slot */}
        <div slot="actions">
          <div 
            style={{
              '--omk-outlined-button-label-text-color': 'var(--color-steel)',
              '--omk-outlined-button-icon-color': 'var(--color-steel)',
              '--omk-outlined-button-outline-color': 'var(--color-silver-mist)',
              '--omk-outlined-button-hover-label-text-color': 'var(--color-deep-jungle)',
              '--omk-outlined-button-hover-icon-color': 'var(--color-deep-jungle)',
              '--omk-outlined-button-hover-state-layer-color': 'var(--color-silver-mist)'
            }}
          >
            <OmButton
              type="button"
              onClick={onPrevious}
              disabled={isLoading}
              buttonType="outlined-button"
              buttonSize="medium"
              leadingIcon="arrow_back"
            >
              Previous
            </OmButton>
          </div>
        </div>
      </OmForm>
    </div>
  );
};

Step5ClaimSummary.propTypes = {
  formData: PropTypes.object.isRequired,
  errors: PropTypes.object,
  isLoading: PropTypes.bool,
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  uploadedFiles: PropTypes.array,
};

Step5ClaimSummary.defaultProps = {
  errors: {},
  isLoading: false,
};

export default Step5ClaimSummary;